"use client";

import styles from "./AlgoliaPostsMosaic.module.scss";
import { Configure } from "react-instantsearch";
import PostsQueryBuilder from "@/lib/algolia/postsQueryBuilder";
import { useMemo } from "react";
import AlgoliaPagination from "@/ui/algolia/AlgoliaPagination";
import PostsMosaicFilters from "./PostsMosaicFilters";
import InternalAlgoliaPostsMosaic from "./InternalAlgoliaPostsMosaic";
import { algoliaIndexNameFor } from "@/lib/algolia/utils";
import { algoliaClient } from "@/lib/algolia/algolia-client";
import {
  createInstantSearchNextInstance,
  InstantSearchNext
} from "react-instantsearch-nextjs";
import { useCustomAlgoliaRouting } from "./use-custom-algolia-routing";

const indexName = algoliaIndexNameFor("posts");
const searchClient = algoliaClient();
const algoliaInstance = createInstantSearchNextInstance();

type Props = {
  title?: string;
  titleComponent?: "h2" | "h3";
  categoryClusterId?: string;
  categoryId?: string;
  authorId?: string;
  tagId?: string;
  limit?: number;
  showTagsFilter: boolean;
  showCategoriesFilter: boolean;
  excludeAIPosts: boolean;
};

const AlgoliaPostsMosaic = ({
  title,
  titleComponent: TitleComponent = "h2",
  categoryClusterId,
  categoryId,
  tagId,
  authorId,
  showTagsFilter,
  showCategoriesFilter,
  excludeAIPosts,
  limit = 10
}: Props) => {
  const query = useMemo(
    () =>
      new PostsQueryBuilder()
        .filterByCategoryId(categoryId)
        .filterByClusterId(categoryClusterId)
        .filterByTagId(tagId)
        .filterByAuthorId(authorId)
        .excludeAIPosts(excludeAIPosts)
        .generate(),
    [categoryId, categoryClusterId, tagId, authorId, excludeAIPosts]
  );

  const routing = useCustomAlgoliaRouting(indexName);

  return (
    <>
      <div className={styles.container}>
        <InstantSearchNext
          indexName={indexName}
          searchClient={searchClient}
          routing={routing}
          instance={algoliaInstance}
          future={{
            preserveSharedStateOnUnmount: false,
            persistHierarchicalRootCount: false
          }}
        >
          <Configure
            distinct={true}
            filters={query}
            hitsPerPage={limit}
            attributesToRetrieve={[
              "objectID",
              "title",
              "authors",
              "publishDate",
              "tags",
              "cluster",
              "mainCategory",
              "slug",
              "featuredMedia"
            ]}
          />
          {title && (
            <TitleComponent className={styles.title}>{title}</TitleComponent>
          )}

          <PostsMosaicFilters
            className={styles.filters}
            showTagsFilter={showTagsFilter}
            showCategoriesFilter={showCategoriesFilter}
          />

          <InternalAlgoliaPostsMosaic />

          <AlgoliaPagination />
        </InstantSearchNext>
      </div>
    </>
  );
};

export default AlgoliaPostsMosaic;
